/**
 * Gestisce la cache LRU dei template THREE.Group con:
 *  - Budget adattivo per device (RAM disponibile stimata)
 *  - Eviction LRU quando si supera il budget
 *  - Monitoring heap JS (Chrome) con eviction preventiva
 *  - Stima dimensione in memoria per ogni modello
 *  - Deduplicazione in-flight per evitare doppi fetch
 *
 * ARCHITETTURA MEMORIA
 * ┌─────────────────────────────────────────────────────────────┐
 * │  JS HEAP (RAM)                                              │
 * │  _cache → Map<key, { group: THREE.Group, sizeBytes, ... }>  │
 * │   ↓ clone(true) per ogni istanza in scena                   │
 * │  furnitureList → mesh istanziati (copie dei template)       │
 * │                                                             │
 * │  Al render: Three.js carica in VRAM                         │
 * │  ┌───────────────────────────────────────┐                  │
 * │  │ VRAM (GPU)                            │                  │
 * │  │  VBO (vertex buffer) per geometria    │                  │
 * │  │  sampler2D per texture                │                  │
 * │  │  (gestito automaticamente da WebGL)   │                  │
 * │  └───────────────────────────────────────┘                  │
 * │                                                             │
 * │  MOBILE (UMA): RAM e VRAM condividono lo stesso pool fisico │
 * └─────────────────────────────────────────────────────────────┘
 */

import { isMobile } from '../utils/device.js';

// Proprietà dei materiali che contengono texture
const TEXTURE_PROPS = [
  'map', 'normalMap', 'roughnessMap', 'metalnessMap',
  'emissiveMap', 'aoMap', 'lightMap', 'bumpMap',
  'displacementMap', 'envMap', 'alphaMap', 'clearcoatMap',
  'clearcoatNormalMap', 'transmissionMap', 'thicknessMap'
];

// Costanti di configurazione
const HEAP_DANGER_RATIO  = 0.82; // 82% heap usato → eviction aggressiva
const HEAP_WARNING_RATIO = 0.70; // 70% heap usato → eviction moderata
const HEAP_CHECK_MS      = 10_000; // Ogni 10 secondi

class ModelCacheManager {

  /**
   * @param {object} options
   * @param {number} [options.maxCacheMB]     Budget massimo in MB (auto se omesso)
   * @param {number} [options.maxEntries]     Max modelli in cache (default 60)
   * @param {boolean} [options.verbose]       Log dettagliati
   */
  constructor(options = {}) {
    this.verbose    = options.verbose ?? false;
    this.maxEntries = options.maxEntries ?? 60;

    // Budget RAM per la cache (solo JS heap, non VRAM)
    this.maxCacheMB = options.maxCacheMB ?? this._detectBudget();

    // LRU via Map (insertion-order): primo elemento = least recently used
    // key → { group, sizeBytes, lastUsed, accessCount, label }
    this._cache = new Map();
    this._totalBytes = 0;

    // In-flight: evita fetch parallele per lo stesso modello
    this._loadingPromises = new Map();

    this._setupHeapMonitor();

    this._log(`Init: budget ${this.maxCacheMB} MB, max ${this.maxEntries} entries`);
  }

  // BUDGET ADATTIVO
  _detectBudget() {
    if (isMobile()) {
      // Stima basata sulla RAM del dispositivo (Navigator API, Chrome 63+)
      // navigator.deviceMemory ritorna 0.25 / 0.5 / 1 / 2 / 4 / 8 GB (arrotondato)
      const deviceGB = navigator.deviceMemory ?? 2; // fallback 2 GB se API non disponibile
      // Usa il 12% della RAM totale stimata con limite massimo a 600 MB per non stressare il garbage collector
      return Math.min(600, Math.floor(deviceGB * 1024 * 0.12));
    }

    // Desktop: usa performance.memory se disponibile (solo Chrome/Edge)
    if (performance?.memory?.jsHeapSizeLimit) {
      const heapLimitMB = performance.memory.jsHeapSizeLimit / (1024 * 1024);
      // Usa al massimo il 80% del limite heap, con limite massimo a 4096 MB
      return Math.min(4096, Math.floor(heapLimitMB * 0.80));
    }

    // Fallback senza API: 2048 MB
    return 2048;
  }

  // STIMA DIMENSIONE MODELLO
  /**
   * Stima la dimensione in RAM del template
   * Include: geometry attributes + index + texture images (CPU-side)
   * Non include la VRAM (buffer WebGL), che viene allocata automaticamente
   * da Three.js al primo render e liberata con .dispose()
   */
  _estimateBytes(group) {
    let bytes = 0;
    const seenTextures = new Set(); // evita doppio conteggio delle texture condivise

    group.traverse(child => {
      if (!child.isMesh) return;

      // Geometria
      const geo = child.geometry;
      if (geo) {
        for (const attr of Object.values(geo.attributes)) {
          if (attr?.array) bytes += attr.array.byteLength;
        }
        if (geo.index?.array) bytes += geo.index.array.byteLength;

        // Morphs
        if (geo.morphAttributes) {
          for (const attrs of Object.values(geo.morphAttributes)) {
            attrs.forEach(a => { if (a?.array) bytes += a.array.byteLength; });
          }
        }
      }

      // Materiali e texture
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach(mat => {
        if (!mat) return;
        TEXTURE_PROPS.forEach(prop => {
          const tex = mat[prop];
          if (!tex || seenTextures.has(tex.uuid)) return;
          seenTextures.add(tex.uuid);

          // CPU-side: l'immagine è in ImageBitmap / HTMLImageElement
          const img = tex.image;
          if (img) {
            const w = img.width  || img.naturalWidth  || 512;
            const h = img.height || img.naturalHeight || 512;
            // RGBA 8-bit = 4 byte/pixel; mipmap ≈ +33%
            bytes += w * h * 4 * 1.33;
          }
        });
      });
    });

    return Math.round(bytes);
  }

  // INTERFACCIA PUBBLICA
  // Restituisce un clone del template, oppure null se non in cache
  get(key) {
    const entry = this._cache.get(key);
    if (!entry) {
      return null;
    }

    entry.lastUsed    = Date.now();
    entry.accessCount++;

    // Sposta in fondo (most recently used)
    this._cache.delete(key);
    this._cache.set(key, entry);

    return entry.group.clone(true);
  }

  /**
   * Aggiunge un template in cache
   * Esegue eviction LRU se necessario prima di inserire
   * Ritorna true se inserito, false se il modello è troppo grande
   */
  set(key, group, label = key) {
    // Non sovrascrivere entry esistenti
    if (this._cache.has(key)) return true;

    const sizeBytes = this._estimateBytes(group);
    const sizeMB    = sizeBytes / (1024 * 1024);
    const budgetBytes = this.maxCacheMB * 1024 * 1024;

    // Se il singolo modello supera il 50% del budget, non cachearlo
    // (verrebbe evicto immediatamente al prossimo inserimento)
    if (sizeBytes > budgetBytes * 0.5) {
      console.warn(
        `[Cache] "${label}" troppo grande per la cache ` +
        `(${sizeMB.toFixed(1)} MB > 50% budget ${this.maxCacheMB} MB). ` +
        `Il modello verrà caricato on-demand senza essere cachato.`
      );
      return false;
    }

    // Evict per fare spazio (prima per budget, poi per entries)
    while (
      (this._totalBytes + sizeBytes > budgetBytes || this._cache.size >= this.maxEntries) &&
      this._cache.size > 0
    ) {
      this._evictLRU();
    }

    this._cache.set(key, {
      group:       group.clone(true),
      sizeBytes,
      lastUsed:    Date.now(),
      accessCount: 0,
      label
    });
    this._totalBytes += sizeBytes;

    this._log(
      `Cached "${label}": ${sizeMB.toFixed(2)} MB | ` +
      `Totale: ${(this._totalBytes / 1024 / 1024).toFixed(1)} / ${this.maxCacheMB} MB ` +
      `(${this._cache.size} entries)`
    );

    return true;
  }

  has(key) {
    return this._cache.has(key);
  }

  // IN-FLIGHT (DEDUPLICAZIONE FETCH)
  getInflight(key)          { return this._loadingPromises.get(key); }
  setInflight(key, promise) { this._loadingPromises.set(key, promise); }
  deleteInflight(key)       { this._loadingPromises.delete(key); }
  hasInflight(key)          { return this._loadingPromises.has(key); }

  // EVICTION
  /**
   * Rimuove il template meno recentemente usato (primo in Map)
   * Libera geometrie e materiali CPU-side
   */
  _evictLRU() {
    const iter = this._cache.entries().next();
    if (iter.done) return;

    const [key, entry] = iter.value;

    this._disposeGroup(entry.group);

    this._totalBytes -= entry.sizeBytes;
    this._cache.delete(key);

    this._log(
      `Evicted "${entry.label}" (${(entry.sizeBytes / 1024 / 1024).toFixed(2)} MB, ` +
      `used ${entry.accessCount}x, idle ${((Date.now() - entry.lastUsed) / 1000).toFixed(0)}s)`
    );
  }
  
  // Evict n% delle entry meno usate. Usato dal monitor heap
  _evictPercent(percent) {
    const toEvict = Math.max(1, Math.floor(this._cache.size * percent));
    for (let i = 0; i < toEvict && this._cache.size > 0; i++) {
      this._evictLRU();
    }
  }

  _disposeGroup(group) {
    if (!group) return;
    group.traverse(child => {
      if (child.geometry) {
        child.geometry.dispose();
      }
      if (child.material) {
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach(mat => {
          if (!mat) return;
          TEXTURE_PROPS.forEach(prop => {
            if (mat[prop]?.dispose) mat[prop].dispose();
          });
          mat.dispose();
        });
      }
    });
  }

  // MONITOR HEAP
  _setupHeapMonitor() {
    if (!performance?.memory) return;

    this._heapTimer = setInterval(() => {
      const used  = performance.memory.usedJSHeapSize;
      const limit = performance.memory.jsHeapSizeLimit;
      const ratio = used / limit;
      const usedMB  = (used  / 1024 / 1024).toFixed(0);
      const limitMB = (limit / 1024 / 1024).toFixed(0);

      if (ratio >= HEAP_DANGER_RATIO) {
        console.warn(
          `[Cache] ⚠️ Heap critico: ${usedMB}/${limitMB} MB (${(ratio * 100).toFixed(0)}%). ` +
          `Eviction aggressiva (50% cache).`
        );
        this._evictPercent(0.50);

      } else if (ratio >= HEAP_WARNING_RATIO) {
        console.warn(
          `[Cache] ⚡ Heap elevato: ${usedMB}/${limitMB} MB (${(ratio * 100).toFixed(0)}%). ` +
          `Eviction moderata (25% cache).`
        );
        this._evictPercent(0.25);
      }
    }, HEAP_CHECK_MS);
  }  

  // PRELOADING CONTROLLATO
  /**
   * Precarica un catalogo con priorità e rispetto del budget
   *
   * @param {object}   catalog
   * @param {GLTFLoader} loader
   * @param {Function} [onProgress]  callback(loaded, total, modelName)
   */
  async preloadCatalog(catalog, loader, onProgress) {
    if (!catalog?.categories) return;

    // Raccoglie tutti i percorsi unici da scaricare
    const queue = [];
    for (const category of catalog.categories) {
      for (const item of category.items) {
        if (item.model && item.type !== 'door' && item.type !== 'window' && !item.isCustom) {
          const path = `/models/gltf/${item.model}`;
          if (!this.has(path) && !this.hasInflight(path)) {
            queue.push({ path, label: item.name });
          }
        }
      }
    }

    if (queue.length === 0) {
      this._log('Preload: tutti i modelli già in cache.');
      return;
    }

    console.log(`[Cache] ⏳ Preload: ${queue.length} modelli in coda...`);

    let loaded = 0;
    let skipped = 0;
    let failed = 0;

    for (const { path, label } of queue) {
      // Stop se non c'è più budget sufficiente (< 20% rimasto)
      const budgetBytes  = this.maxCacheMB * 1024 * 1024;
      const remainRatio  = 1 - this._totalBytes / budgetBytes;
      if (remainRatio < 0.20) {
        console.warn(
          `[Cache] Preload interrotto: budget quasi esaurito ` +
          `(${(this._totalBytes / 1024 / 1024).toFixed(0)} / ${this.maxCacheMB} MB). ` +
          `${queue.length - loaded - skipped - failed} modelli non precaricati.`
        );
        break;
      }

      // Skip se già in cache (aggiunto da un drop avvenuto durante il preload)
      if (this.has(path)) {
        skipped++;
        continue;
      }

      // Se c'è già un fetch in corso, aspettalo
      if (this.hasInflight(path)) {
        try { await this.getInflight(path); } catch (_) {}
        skipped++;
        continue;
      }

      try {
        await this._loadAndCache(path, label, loader);
        loaded++;
        onProgress?.(loaded, queue.length, label);
      } catch (err) {
        failed++;
        console.warn(`[Cache] Preload fallito per "${label}": ${err.message}`);
      }
    }

    console.log(
      `[Cache] ✓ Preload completato: ${loaded} ok, ${skipped} skip, ${failed} errori. ` +
      `Cache: ${(this._totalBytes / 1024 / 1024).toFixed(1)} / ${this.maxCacheMB} MB`
    );
  }

  /**
   * Carica un singolo modello e lo mette in cache
   * Usato sia dal preload che dal load on-demand
   */
  _loadAndCache(path, label, loader) {
    // Già in cache
    if (this.has(path)) return Promise.resolve();

    // Già in volo
    if (this.hasInflight(path)) return this.getInflight(path);

    const promise = new Promise((resolve, reject) => {
      loader.load(
        path,
        (gltf) => {
          this.set(path, gltf.scene, label);
          this.deleteInflight(path);
          resolve();
        },
        undefined,
        (err) => {
          this.deleteInflight(path);
          reject(err);
        }
      );
    });

    this.setInflight(path, promise);
    return promise;
  }

  // PULIZIA
  clear() {
    // Copia le chiavi per evitare modifica-durante-iterazione
    for (const key of [...this._cache.keys()]) {
      const entry = this._cache.get(key);
      this._disposeGroup(entry.group);
      this._cache.delete(key);
    }
    this._totalBytes = 0;
    this._loadingPromises.clear();
    console.log('[Cache] ✓ Cache svuotata e risorse liberate.');
  }

  dispose() {
    this.clear();
    if (this._heapTimer) clearInterval(this._heapTimer);
  }

  _log(msg) {
    if (this.verbose) console.log(`[Cache] ${msg}`);
  }
}

// SINGLETON
// Un'unica istanza condivisa da tutti i moduli (Furniture, preloader, ecc.)
export const modelCacheManager = new ModelCacheManager();
export default ModelCacheManager;